import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { styled } from 'styled-components';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { Flex } from '@fabric-app/ui/components/Flex';
import Badge from '@fabric-app/ui/components/Badge';
import Skeleton from '@fabric-app/ui/components/Skeleton';
import { P } from '@fabric-app/ui/components/Typography';
import { formatMinutesSeconds, formatHoursMinutesSeconds } from '@fabric-app/utils/number';
import { isDefined } from '@fabric-app/utils/guards';
import React__default, { useState, useMemo } from 'react';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import Select from '@fabric-app/ui/components/Select';
import { heuristicTranscriptLines } from '../utils/heuristicTranscriptLines.js';
import { oneOfGroup } from '../../user/utils/group.js';
import { UserGroup } from '@fabric/woody-client';
import { captureException } from '@fabric-app/compat/capabilities/sentry';

const TranscriptContainer = styled(Flex).attrs({
  direction: "column",
  gap: "sectionsShort"
}).withConfig({
  displayName: "Transcript__TranscriptContainer",
  componentId: "sc-c7805878-0"
})([
  `padding:1rem;background-color:`,
  `;border-radius:6px;width:100%;max-width:520px;`
], cssVar["color-bg-primary"]);
const TranscriptGrid = styled.div.withConfig({
  displayName: "Transcript__TranscriptGrid",
  componentId: "sc-c7805878-1"
})([
  `display:grid;grid-template-columns:auto 1fr;gap:0.75rem 1.875rem;`
]);
const TranscriptBadge = styled(Badge.Static).attrs((p) => {
  return {
    size: "small+",
    variant: p.clickable ? "app-primary" : "bg-tertiary",
    ...p
  };
}).withConfig({
  displayName: "Transcript__TranscriptBadge",
  componentId: "sc-c7805878-2"
})([
  `width:100%;font-variant-numeric:tabular-nums;`
]);
const Transcript = ({ transcriptLines: transcriptLinesProp, onTranscriptLineClick, currentTimestamp }) => {
  const { data: user } = useQueryUserInfo();
  const isDev = user?.email.endsWith("@fabric.so") || oneOfGroup(user, [
    UserGroup.ADMIN
  ]);
  const [useTranscriptHeuristic, setUseTranscriptHeuristic] = useState(true);
  const transcriptLines = useMemo(() => {
    try {
      return useTranscriptHeuristic ? heuristicTranscriptLines(transcriptLinesProp) : transcriptLinesProp;
    } catch (error) {
      captureException(error);
      return transcriptLinesProp;
    }
  }, [
    useTranscriptHeuristic,
    transcriptLinesProp
  ]);
  const currentTranscriptCue = useMemo(() => {
    return !isDefined(currentTimestamp) ? void 0 : transcriptLines.find((cue) => cue.startTime <= currentTimestamp && cue.endTime >= currentTimestamp);
  }, [
    currentTimestamp,
    transcriptLines
  ]);
  const formatFunction = useMemo(() => {
    const lastTranscriptCue = transcriptLines[transcriptLines.length - 1];
    if (!lastTranscriptCue) {
      return formatMinutesSeconds;
    }
    if (lastTranscriptCue.endTime >= 36e3) {
      return (time) => formatHoursMinutesSeconds(time, true);
    }
    if (lastTranscriptCue.endTime >= 3600) {
      return (time) => formatHoursMinutesSeconds(time, false);
    }
    if (lastTranscriptCue.endTime >= 600) {
      return (time) => formatMinutesSeconds(time, true);
    }
    return (time) => formatMinutesSeconds(time, false);
  }, [
    transcriptLines
  ]);
  return /* @__PURE__ */ jsxs(TranscriptContainer, {
    children: [
      /* @__PURE__ */ jsxs(Flex, {
        justifyContent: "space-between",
        children: [
          /* @__PURE__ */ jsx(P, {
            size: "lg",
            children: "Transcript"
          }),
          isDev && /* @__PURE__ */ jsxs(Select, {
            value: useTranscriptHeuristic ? "heuristic" : "raw",
            onChange: (value) => setUseTranscriptHeuristic(value === "heuristic"),
            selectVariant: "discreet",
            children: [
              /* @__PURE__ */ jsx(Select.ItemOption, {
                value: "raw",
                children: "Raw"
              }),
              /* @__PURE__ */ jsx(Select.ItemOption, {
                value: "heuristic",
                children: "Heuristic"
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsxs(TranscriptGrid, {
        children: [
          transcriptLines.length === 0 && /* @__PURE__ */ jsxs(Fragment, {
            children: [
              /* @__PURE__ */ jsx(Skeleton, {
                width: 59.48,
                height: 19,
                borderRadius: 6
              }),
              /* @__PURE__ */ jsx(Skeleton, {
                width: "80%",
                height: 19
              }),
              /* @__PURE__ */ jsx(Skeleton, {
                width: 59.48,
                height: 19,
                borderRadius: 6
              }),
              /* @__PURE__ */ jsx(Skeleton, {
                width: "50%",
                height: 38
              }),
              /* @__PURE__ */ jsx(Skeleton, {
                width: 59.48,
                height: 19,
                borderRadius: 6
              }),
              /* @__PURE__ */ jsx(Skeleton, {
                width: "70%",
                height: 19
              }),
              /* @__PURE__ */ jsx(Skeleton, {
                width: 59.48,
                height: 19,
                borderRadius: 6
              }),
              /* @__PURE__ */ jsx(Skeleton, {
                width: "80%",
                height: 19
              }),
              /* @__PURE__ */ jsx(Skeleton, {
                width: 59.48,
                height: 19,
                borderRadius: 6
              }),
              /* @__PURE__ */ jsx(Skeleton, {
                width: "60%",
                height: 19
              }),
              /* @__PURE__ */ jsx(Skeleton, {
                width: 59.48,
                height: 19,
                borderRadius: 6
              }),
              /* @__PURE__ */ jsx(Skeleton, {
                width: "90%",
                height: 57
              })
            ]
          }),
          transcriptLines && transcriptLines.length > 0 && transcriptLines.map((cue) => /* @__PURE__ */ jsxs(React__default.Fragment, {
            children: [
              /* @__PURE__ */ jsx(Flex, {
                alignItems: "flex-start",
                children: /* @__PURE__ */ jsx(TranscriptBadge, {
                  onClick: () => onTranscriptLineClick?.(cue),
                  clickable: isDefined(onTranscriptLineClick),
                  children: formatFunction(cue.startTime)
                })
              }),
              /* @__PURE__ */ jsx(Flex, {
                children: /* @__PURE__ */ jsx(P, {
                  size: "sm",
                  color: currentTranscriptCue?.hash === cue.hash ? "primary" : "tertiary",
                  children: cue.text
                })
              })
            ]
          }, cue.hash))
        ]
      })
    ]
  });
};

export { Transcript };
//# sourceMappingURL=Transcript.js.map
